import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import parse from 'html-react-parser';
import useWindowWidth from '../../../../hooks/useWindowWidth';
import { joyrideStyles } from '../../../../utils/joyrideStyles';
import { useSelector } from 'react-redux';
import { examsSelectors } from '../../../../slices/exams/examsSlice';

interface FlashcardsTourProps {
  stepTargets: (React.RefObject<HTMLElement> | string)[];
  stepFunctions: (() => void)[];
  run: boolean;
  stepIndex: number;
  onStepChange: (newIndex: number) => void;
  onTourEnd?: () => void;
  handleSeenFlashcardsTour: () => void;
  isFlipped: boolean;
}

const FlashcardsFeatureTour: React.FC<FlashcardsTourProps> = ({
  stepTargets,
  stepFunctions,
  run,
  stepIndex,
  onStepChange,
  onTourEnd,
  handleSeenFlashcardsTour,
  isFlipped,
}) => {
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  const [steps, setSteps] = useState<Step[]>([]);
  const [ready, setReady] = useState(false);
  const windowWidth = useWindowWidth();
  const placementsDesktop = ['center', 'top', 'top', 'right'];
  const placementsMobile = ['auto', 'auto', 'center', 'top'];

  const renderContents = (index: number) => {
    if (index === 0 && primaryExam?.isUsingQbank) {
      const text = `These are spaced repetition flashcards.<br><br>New cards are assigned by QBank questions you get wrong.<br><br>Due cards are scheduled with an AI-optimized algorithm that learns your memory patterns.`;
      return parse(text);
    }
    if (index === 0 && !primaryExam?.isUsingQbank) {
      const text = `These are spaced repetition flashcards.<br><br>New cards are assigned by topic yield.<br><br>Due cards are scheduled with an AI-optimized algorithm that learns your memory patterns.`;
      return parse(text);
    }
    if (index === 1) {
      const text =
        'Think of the answer to this question. When you are ready to see the answer, click Reveal or hit space.';
      return parse(text);
    }
    if (index === 2) {
      const text =
        'Now assess your answer. You will see cards you get wrong sooner than cards you get right.';
      return parse(text);
    }
  };

  useEffect(() => {
    if (stepIndex === 0) {
      handleSeenFlashcardsTour();
    }
  }, [stepIndex]);

  useEffect(() => {
    if (stepIndex === 1 && isFlipped) {
      setTimeout(() => {
        onStepChange(stepIndex + 1);
      }, 250);
    }
    if (stepIndex === 2 && !isFlipped) {
      setTimeout(() => {
        onStepChange(stepIndex + 1);
      }, 250);
    }
  }, [isFlipped]);

  useEffect(() => {
    const areTargetsReady = stepTargets.every((target) => {
      if (typeof target === 'string') {
        return true;
      } else {
        return target.current !== null;
      }
    });
    if (areTargetsReady) {
      // @ts-expect-error - TS doesn't know about all step props
      const newSteps: Step[] = stepTargets.map((target, index) => {
        const stepTarget =
          typeof target === 'string' ? target : target.current!;
        return {
          target: stepTarget,
          placement:
            windowWidth > 900
              ? placementsDesktop[index]
              : placementsMobile[index],
          disableBeacon: true,
          content: (
            <div className="tour-wrapper">
              <div className="tour-wrapper_header">
                <div className="step-number">
                  {index + 1} <span>of {steps.length}</span>
                </div>
                <div className="steps">
                  {[...Array(steps.length)].map((_, i) => (
                    <div
                      key={i}
                      className={`steps_step ${i === index ? 'active' : ''}`}
                    ></div>
                  ))}
                </div>
              </div>
              <div className="tour-wrapper_content">
                {renderContents(index)}
              </div>
              <div className="blur-1"></div>
              <div className="blur-2"></div>
              <div className="tour-wrapper_footer">
                {(index === 0 || index === 1 || index === 2) && (
                  <>
                    {index !== 1 && index !== 2 && (
                      <button
                        onClick={() => {
                          onStepChange(stepIndex + 1);
                          const floater = document.querySelector(
                            '.__floater'
                          ) as HTMLDivElement;
                          if (floater) {
                            floater.style.display = 'none';
                          }
                        }}
                        className="button button--glass"
                      >
                        Next
                      </button>
                    )}
                    {index !== 2 && (
                      <button
                        className="button button--skip"
                        onClick={() => onTourEnd?.()}
                      >
                        Skip Tour
                      </button>
                    )}
                  </>
                )}
                {index === 2 && (
                  <button
                    onClick={() => {
                      () => onTourEnd?.();
                      const floater = document.querySelector(
                        '.__floater'
                      ) as HTMLDivElement;
                      if (floater) {
                        floater.style.display = 'none';
                      }
                    }}
                    className="button button--glass"
                    style={{ marginBottom: '1rem' }}
                  >
                    Finish
                  </button>
                )}
              </div>
            </div>
          ),
        };
      });
      setSteps(newSteps);
      setReady(true);
    }
  }, [stepTargets, onStepChange, onTourEnd]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { index, type, status } = data;

    if (type === 'step:before') {
      if (stepFunctions[index]) {
        stepFunctions[index]();
      }
    }

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      if (onTourEnd) {
        onTourEnd();
      }
    }
  };

  if (!ready) {
    return null;
  }
  return (
    <>
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        continuous
        hideCloseButton={true}
        hideBackButton={true}
        disableScrolling={true}
        disableOverlay
        styles={joyrideStyles}
      />
    </>
  );
};

export default FlashcardsFeatureTour;
